import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Zap, Crown } from 'lucide-react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const Plans = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/plans');
      setPlans(response.data.plans);
    } catch (error) {
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (planId) => {
    if (!user) {
      toast.error('Debés iniciar sesión para adquirir un plan');
      navigate('/login');
      return;
    }

    setPurchasing(planId);
    try {
      const response = await api.post('/plans/purchase', { plan_id: planId });
      toast.success(response.data.message);
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la compra');
    } finally {
      setPurchasing(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'inicial': return <Star className="h-8 w-8" />;
      case 'basic': return <Zap className="h-8 w-8" />;
      case 'socio': return <Crown className="h-8 w-8" />;
      default: return <Star className="h-8 w-8" />;
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-6 md:px-12 lg:px-24" data-testid="plans-page">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tight mb-4">
            Planes y Membresías
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Elegí el plan que mejor se adapte a tus necesidades y comenzá a mejorar tu juego
          </p>
        </div>

        {user && (
          <div className="mb-8 p-4 bg-primary/10 border border-primary/30 rounded-lg text-center">
            <p className="text-sm text-muted-foreground">Tu plan actual:</p>
            <p className="text-xl font-bold text-primary">
              {user.plan === 'none' ? 'Sin plan activo' : 
               user.plan === 'inicial' ? 'Plan Inicial' :
               user.plan === 'basic' ? 'Plan Basic' : 'Plan Socio'}
            </p>
            <p className="text-sm">Créditos disponibles: <span className="font-bold text-primary">{user.credits || 0}</span></p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative glass-effect border-white/10 transition-all hover:scale-105 ${
                plan.highlighted ? 'border-primary shadow-lg shadow-primary/20' : ''
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-bold">
                    RECOMENDADO
                  </span>
                </div>
              )}
              
              <CardHeader className="text-center pt-8">
                <div className={`mx-auto mb-4 p-3 rounded-full ${
                  plan.highlighted ? 'bg-primary text-primary-foreground' : 'bg-white/10'
                }`}>
                  {getPlanIcon(plan.id)}
                </div>
                <CardTitle className="text-2xl font-heading uppercase">{plan.name}</CardTitle>
                <CardDescription className="text-sm">{plan.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="text-center">
                <div className="mb-6">
                  {plan.price ? (
                    <>
                      <span className="text-4xl font-bold">{formatPrice(plan.price)}</span>
                      <span className="text-muted-foreground">/{plan.duration || 'mes'}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold">{formatPrice(plan.price_per_hour)}</span>
                      <span className="text-muted-foreground">/hora</span>
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-6 text-left">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                        plan.highlighted ? 'text-primary' : 'text-green-500'
                      }`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.ai_access && (
                  <div className="mb-4 p-2 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-300">
                    ✨ Incluye acceso a análisis con IA
                  </div>
                )}

                <Button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={purchasing === plan.id || (user?.plan === plan.id)}
                  className={`w-full font-bold uppercase ${
                    plan.highlighted 
                      ? 'bg-primary hover:bg-primary/90' 
                      : 'bg-white/10 hover:bg-white/20'
                  }`}
                  data-testid={`purchase-${plan.id}`}
                >
                  {purchasing === plan.id ? 'Procesando...' : 
                   user?.plan === plan.id ? 'Plan Actual' : 'Elegir Plan'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>¿Tenés dudas sobre qué plan elegir?</p>
          <p>Contactanos a <a href="mailto:info@cityvirtualgolf.com" className="text-primary hover:underline">info@cityvirtualgolf.com</a></p>
        </div>
      </div>
    </div>
  );
};
